import type { Bet } from '@urbanrats/contracts';
import { promoPayout } from '@urbanrats/domain';
import type { OpsBet, OpsCallback, OpsData, OpsTx } from './model.ts';
import { PerCurrency } from './money.ts';

const ACCEPTED_OR_LATER = new Set(['ACCEPTED', 'RESULT_WON', 'RESULT_LOST', 'CREDIT_PENDING', 'SETTLED', 'REFUND_PENDING', 'VOIDED']);

/** Which bets a KPI is made of: the UI links every figure to this list (drilldown). */
export interface BetBasis {
  state?: string;
  funding?: 'CASH' | 'FREEBET';
  basis: string;
}

export interface FinanceRow {
  currency: string;
  scale: number;
  /** Cash debited for accepted bets (incl. later refunded). */
  cashStakeAccepted: bigint;
  settledCashStake: bigint;
  settledCashPayout: bigint;
  /** Proposed GGR: settled cash stake − settled cash payout; refunded stakes excluded; promo cost separate. */
  ggr: bigint;
  /** Face value of settled freebet stakes (no cash). */
  promoStake: bigint;
  /** Credits paid on freebet bets + payout uplift from Lightning/boosts on cash bets. */
  promoCost: bigint;
  freebetCredits: bigint;
  lightningBoostUplift: bigint;
  refunds: bigint;
  pendingCredit: bigint;
  pendingRefund: bigint;
  openCashStake: bigint;
  betCount: number;
}

const cash = (b: Bet) => b.funding === 'CASH';

/** Payout the bet would have had without Lightning/boost (to separate promo cost from game payout). */
function basePayout(b: Bet): bigint {
  if (!b.quote || !b.payoutGross) return 0n;
  const won = b.state === 'SETTLED' || b.state === 'CREDIT_PENDING' ? BigInt(b.payoutGross.amountAtomic) > 0n : false;
  if (!won) return 0n;
  return BigInt(promoPayout({ stake: b.stake, odds: b.quote.odds, lightning: null, boostBps: 0, cap: b.quote.payoutCap, funding: { kind: 'CASH' } }, true).credit.amountAtomic);
}

export function financeByCurrency(bets: readonly OpsBet[]): FinanceRow[] {
  const rows = new Map<string, FinanceRow>();
  const row = (c: string, scale: number): FinanceRow => {
    let r = rows.get(c);
    if (!r) {
      r = { currency: c, scale, cashStakeAccepted: 0n, settledCashStake: 0n, settledCashPayout: 0n, ggr: 0n, promoStake: 0n, promoCost: 0n, freebetCredits: 0n, lightningBoostUplift: 0n, refunds: 0n, pendingCredit: 0n, pendingRefund: 0n, openCashStake: 0n, betCount: 0 };
      rows.set(c, r);
    }
    return r;
  };
  for (const { bet: b } of bets) {
    const r = row(b.stake.currency, b.stake.scale);
    if (!ACCEPTED_OR_LATER.has(b.state)) continue;
    r.betCount++;
    const stake = BigInt(b.stake.amountAtomic);
    const credit = b.payoutGross ? BigInt(b.payoutGross.amountAtomic) : 0n;
    if (cash(b)) r.cashStakeAccepted += stake;
    if (b.state === 'SETTLED') {
      if (cash(b)) {
        r.settledCashStake += stake;
        r.settledCashPayout += credit;
        r.lightningBoostUplift += credit - basePayout(b);
      } else {
        r.promoStake += stake;
        r.freebetCredits += credit;
      }
    }
    if (b.state === 'VOIDED' && cash(b)) r.refunds += stake;
    if (b.state === 'CREDIT_PENDING') r.pendingCredit += credit;
    if (b.state === 'REFUND_PENDING' && cash(b)) r.pendingRefund += stake;
    if (['ACCEPTED', 'RESULT_WON', 'RESULT_LOST'].includes(b.state) && cash(b)) r.openCashStake += stake;
  }
  for (const r of rows.values()) {
    r.ggr = r.settledCashStake - r.settledCashPayout;
    r.promoCost = r.freebetCredits + r.lightningBoostUplift;
  }
  return [...rows.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/** Straight sums of successful ledger entries per currency (the fixture ledger). */
export function ledgerTotals(txs: readonly OpsTx[]) {
  const debits = new PerCurrency();
  const credits = new PerCurrency();
  const rollbacks = new PerCurrency();
  for (const x of txs) {
    if (x.state !== 'SUCCEEDED') continue;
    (x.operation === 'DEBIT' ? debits : x.operation === 'CREDIT' ? credits : rollbacks).add(x.amount);
  }
  return { debits, credits, rollbacks };
}

export interface ReconIssue {
  kind: 'BALANCE_MISMATCH' | 'MISSING_DEBIT' | 'MISSING_CREDIT' | 'MISSING_ROLLBACK' | 'ORPHAN_TX';
  ref: string;
  detail: string;
}

export interface ReconRow {
  playerRef: string;
  operatorId: string;
  brandId: string;
  currency: string;
  scale: number;
  initial: bigint;
  debits: bigint;
  credits: bigint;
  rollbacks: bigint;
  expected: bigint;
  actual: bigint;
  ok: boolean;
}

/**
 * Wallet reconciliation: balance = initial − debits + credits + rollbacks for every player, and every bet
 * has the ledger entries its state requires. Pending items are listed, not treated as mismatches.
 */
export function reconcile(d: Pick<OpsData, 'players' | 'bets' | 'txs'>) {
  const rows: ReconRow[] = d.players.map((p) => {
    const mine = d.txs.filter((x) => x.playerRef === p.playerRef && x.state === 'SUCCEEDED');
    const sum = (op: string) => mine.filter((x) => x.operation === op).reduce((a, x) => a + BigInt(x.amount.amountAtomic), 0n);
    const initial = BigInt(p.initialBalance.amountAtomic);
    const debits = sum('DEBIT');
    const credits = sum('CREDIT');
    const rollbacks = sum('ROLLBACK');
    const expected = initial - debits + credits + rollbacks;
    const actual = BigInt(p.balance.amountAtomic);
    return { playerRef: p.playerRef, operatorId: p.operatorId, brandId: p.brandId, currency: p.balance.currency, scale: p.balance.scale, initial, debits, credits, rollbacks, expected, actual, ok: expected === actual };
  });
  const issues: ReconIssue[] = rows.filter((r) => !r.ok).map((r) => ({ kind: 'BALANCE_MISMATCH', ref: r.playerRef, detail: `expected ${r.expected}, ledger ${r.actual}` }));
  const txByKey = new Map(d.txs.map((x) => [`${x.playerRef}/${x.transactionId}`, x]));
  const has = (b: OpsBet, id: string) => txByKey.get(`${b.playerRef}/${id}`)?.state === 'SUCCEEDED';
  const pending: { ref: string; state: string }[] = [];
  for (const b of d.bets) {
    const id = b.bet.betId;
    if (cash(b.bet) && ACCEPTED_OR_LATER.has(b.bet.state) && !has(b, `debit:${id}`)) issues.push({ kind: 'MISSING_DEBIT', ref: b.key, detail: b.bet.state });
    if (b.bet.state === 'SETTLED' && !has(b, `credit:${id}`)) issues.push({ kind: 'MISSING_CREDIT', ref: b.key, detail: 'settled without credit' });
    if (b.bet.state === 'VOIDED' && cash(b.bet) && !has(b, `rollback:${id}`)) issues.push({ kind: 'MISSING_ROLLBACK', ref: b.key, detail: 'voided without rollback' });
    if (b.bet.state === 'CREDIT_PENDING' || b.bet.state === 'REFUND_PENDING') pending.push({ ref: b.key, state: b.bet.state });
  }
  const betKeys = new Set(d.bets.map((b) => b.key));
  for (const x of d.txs) if (x.betKey && !betKeys.has(x.betKey)) issues.push({ kind: 'ORPHAN_TX', ref: x.key, detail: x.transactionId });
  return { rows, issues, pending };
}

export function callbackStats(cbs: readonly OpsCallback[]) {
  const done = cbs.filter((c) => c.outcome !== 'PENDING');
  const lat = done.filter((c) => c.outcome === 'OK' && c.latencyMs !== null).map((c) => c.latencyMs!).sort((a, b) => a - b);
  const p95 = lat.length ? lat[Math.min(lat.length - 1, Math.ceil(lat.length * 0.95) - 1)]! : null;
  return {
    attempts: cbs.length,
    ok: cbs.filter((c) => c.outcome === 'OK').length,
    timeouts: cbs.filter((c) => c.outcome === 'TIMEOUT').length,
    retries: cbs.filter((c) => c.attempt > 1).length,
    pending: cbs.filter((c) => c.outcome === 'PENDING').length,
    p95LatencyMs: p95,
    simulated: cbs.some((c) => c.simulated),
  };
}
